import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Pencil, Trash2, Plus, Network, Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useData } from "../lib/DataContext";
import { PageHeader, Panel, TierBadge, Qty } from "../components/shared";
import BomEditor from "../components/BomEditor";
import BomFlowchartModal from "../components/BomFlowchartModal";
import ItemFactSheet from "../components/ItemFactSheet";
import ProductionTimes from "../components/ProductionTimes";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const Btn = ({ children, testId, onClick, danger }) => (
  <button data-testid={testId} onClick={onClick}
    className={`h-12 px-4 rounded-md border text-sm font-semibold flex items-center gap-2 ${danger ? "border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10" : "border-[#334155] hover:border-[#F59E0B]"}`}>{children}</button>
);

export default function Boms() {
  const { boms, itemsById, refreshAll } = useData();
  const [selId, setSelId] = useState(null);
  const [editor, setEditor] = useState(null);
  const [flow, setFlow] = useState(false);
  const [fact, setFact] = useState(null);
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const reviewCount = boms.filter((b) => b.needs_review).length;
  const reviewOnly = params.get("review") === "1" && reviewCount > 0;
  const sorted = [...boms].sort((a, b) => (itemsById[b.product_id]?.tier || 0) - (itemsById[a.product_id]?.tier || 0) || a.name.localeCompare(b.name));
  const shown = sorted.filter((b) => { const p = itemsById[b.product_id]; return (!reviewOnly || b.needs_review) && (!q || `${b.name} ${p?.name || ""} ${p?.sku || ""}`.toLowerCase().includes(q.toLowerCase())); });
  const sel = boms.find((b) => b.id === selId) || shown[0] || sorted[0];

  const del = async () => {
    if (!window.confirm(`Delete ${sel.name}?`)) return;
    try { await api.delete(`/boms/${sel.id}`); toast.success("BOM deleted"); setSelId(null); refreshAll(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader kicker="// BILLS OF MATERIALS" title="BOM Templates">
        <button data-testid="bom-new-btn" onClick={() => setEditor({ mode: "new", bom: null })}
          className="h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center gap-2"><Plus className="w-4 h-4" />New BOM</button>
      </PageHeader>
      <Tabs defaultValue="templates">
        <TabsList className="h-auto flex-wrap bg-[#0F1623] border border-[#1E293B] mb-6">
          <TabsTrigger value="templates" data-testid="bom-tab-templates" className="h-10 px-5 data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#090D16]">Templates</TabsTrigger>
          <TabsTrigger value="times" data-testid="bom-tab-times" className="h-10 px-5 data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#090D16]">Production Times</TabsTrigger>
        </TabsList>
        <TabsContent value="times"><ProductionTimes /></TabsContent>
        <TabsContent value="templates">
      {reviewCount > 0 && (
        <button data-testid="bom-review-alert" onClick={() => setParams(reviewOnly ? {} : { review: "1" })}
          className={`mb-4 w-full flex items-center gap-3 p-4 rounded-md border-2 font-semibold text-left transition-colors border-[#F59E0B] text-[#F59E0B] ${reviewOnly ? "bg-[#F59E0B]/15" : "hover:bg-[#172033]"}`}>
          <Copy className="w-5 h-5 shrink-0" /><span className="font-display font-extrabold">COPY - NEEDS REVIEW</span>
          <span className="text-sm text-[#9CA3AF] font-normal" data-testid="bom-review-count">{reviewCount} duplicated BOM{reviewCount === 1 ? "" : "s"} to check. Open Edit and save to clear.</span>
          <span className="ml-auto font-mono text-xs">{reviewOnly ? "SHOW ALL" : "FILTER"}</span>
        </button>
      )}
      <div className="grid lg:grid-cols-[340px_1fr] gap-6 min-w-0">
        <Panel className="p-2 max-h-[45vh] lg:max-h-[70vh] overflow-y-auto min-w-0" data-testid="bom-list">
          <div className="relative mb-2 sticky top-0 z-10 bg-[#0F1623]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
            <input data-testid="bom-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search BOM, product or SKU…"
              className="w-full h-12 pl-9 pr-3 bg-[#090D16] border border-[#334155] rounded-md text-sm focus:outline-none focus:border-[#F59E0B]" />
          </div>
          {!shown.length && <div data-testid="bom-search-empty" className="p-4 font-mono text-xs text-[#6B7280]">NO BOMs MATCH</div>}
          {shown.map((b) => {
            const p = itemsById[b.product_id];
            return (
              <button key={b.id} data-testid={`bom-list-item-${p?.sku}`} onClick={() => setSelId(b.id)}
                className={`w-full text-left p-4 rounded-md flex flex-col gap-2 ${sel?.id === b.id ? "bg-[#172033] border-l-4 border-[#F59E0B]" : "hover:bg-[#111827]"}`}>
                {p && <TierBadge tier={p.tier} />}
                <span className="font-semibold">{b.name}</span>
                {b.needs_review && <span data-testid={`bom-copy-flag-${p?.sku}`} className="self-start font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-[#F59E0B]/15 text-[#F59E0B]">COPY · NEEDS REVIEW</span>}
                <span className="font-mono text-[11px] text-[#6B7280]">{b.components.length} components</span>
              </button>
            );
          })}
        </Panel>
        {sel && (
          <Panel className="p-4 sm:p-6 rise min-w-0" key={sel.id} data-testid="bom-detail">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <div className="min-w-0">
                <div className="font-mono text-xs text-[#6B7280]">PRODUCES</div>
                <h2 className="font-display text-2xl font-bold break-words" data-testid="bom-detail-name">{sel.name}</h2>
                {sel.needs_review && <div data-testid="bom-detail-copy-flag" className="mt-1 text-xs text-[#F59E0B] font-mono">COPY · NEEDS REVIEW. Open Edit and save to confirm this recipe.</div>}
                <div className="text-[#9CA3AF] text-sm">{itemsById[sel.product_id]?.name}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button data-testid="bom-duplicate-btn" onClick={() => setEditor({ mode: "duplicate", bom: sel })}
                  className="h-12 px-5 rounded-md bg-[#FF5722] text-white font-bold flex items-center gap-2 hover:bg-[#FF7043]"><Copy className="w-4 h-4" />Duplicate BOM</button>
                <Btn testId="bom-flowchart-btn" onClick={() => setFlow(true)}><Network className="w-4 h-4" />View BOM Flowchart</Btn>
                <Btn testId="bom-edit-btn" onClick={() => setEditor({ mode: "edit", bom: sel })}><Pencil className="w-4 h-4" />Edit</Btn>
                <Btn testId="bom-delete-btn" onClick={del} danger><Trash2 className="w-4 h-4" /></Btn>
              </div>
            </div>
            <div className="divide-y divide-[#1E293B] border border-[#1E293B] rounded-md">
              {sel.components.map((c) => {
                const it = itemsById[c.item_id];
                if (!it) return null;
                return (
                  <div key={c.item_id} data-testid={`bom-component-${it.sku}`} className="flex flex-wrap sm:flex-nowrap items-center gap-3 px-3 sm:px-4 py-4">
                    <TierBadge tier={it.tier} />
                    <div className="flex-1 min-w-0"><div className="font-medium truncate">{it.name}</div><div className="font-mono text-[11px] text-[#6B7280]">{it.sku} · stock {it.stock}</div></div>
                    <Qty v={c.qty} unit={it.unit} className="text-lg font-bold text-[#F59E0B]" />
                  </div>
                );
              })}
            </div>
          </Panel>
        )}
      </div>
        </TabsContent>
      </Tabs>
      <BomFlowchartModal item={flow && sel ? itemsById[sel.product_id] : null} onClose={() => setFlow(false)} onOpenItem={(id) => { setFlow(false); setFact(id); }} />
      {fact && <ItemFactSheet itemId={fact} onClose={() => setFact(null)} />}
      {editor && <BomEditor mode={editor.mode} bom={editor.bom} onClose={() => setEditor(null)} />}
    </div>
  );
}
